import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  Collapse,
  Skeleton,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  ReceiptLong,
} from '@mui/icons-material';
import buyerClosingCostsService from '../../api/services/buyerClosingCosts';
import { formatCurrency } from '../../utils/formatCurrency';

export type FeePreviewPaymentMethod = 'Financed' | 'VA' | 'Cash' | '';
export type FeePreviewInspectionMode = 'required' | 'informational' | 'none';

interface BrokerFeePreviewProps {
  price: number;
  paymentMethod: FeePreviewPaymentMethod;
  inspection: FeePreviewInspectionMode;
  hasHomeSaleContingency: boolean;
}

interface BrokerFeePricing {
  baseRate: number;
  vaModifier: number;
  cashDiscount: number;
  inspectionModifier: number;
  homeSaleContingencyModifier: number;
  minimumFee: number;
}

interface BrokerFeeState {
  brokerFee: number;
  brokerFeeEffectiveRate: number;
  brokerFeeIsMinimum: boolean;
  brokerFeePricing: BrokerFeePricing | null;
}

const FEE_BREAKDOWN_ID = 'broker-fee-breakdown';

const formatPercent = (rate: number, signed = false) => {
  const pct = (rate * 100).toFixed(2);
  if (!signed) return `${pct}%`;
  return rate >= 0 ? `+${pct}%` : `−${(-rate * 100).toFixed(2)}%`;
};

const buildModifierBreakdown = (
  paymentMethod: FeePreviewPaymentMethod,
  inspection: FeePreviewInspectionMode,
  hasHomeSaleContingency: boolean,
  pricing: BrokerFeePricing | null
) => {
  if (!pricing) return [];
  const items: { label: string; rate: string; positive: boolean }[] = [
    { label: 'Base rate', rate: formatPercent(pricing.baseRate), positive: true },
  ];
  if (paymentMethod === 'VA') {
    items.push({
      label: 'VA loan',
      rate: formatPercent(pricing.vaModifier, true),
      positive: true,
    });
  } else if (paymentMethod === 'Cash') {
    items.push({
      label: 'Cash discount',
      rate: formatPercent(-pricing.cashDiscount, true),
      positive: false,
    });
  }
  if (inspection !== 'none') {
    items.push({
      label:
        inspection === 'informational'
          ? 'Inspection (informational)'
          : 'Inspection',
      rate: formatPercent(pricing.inspectionModifier, true),
      positive: true,
    });
  }
  if (hasHomeSaleContingency) {
    items.push({
      label: 'Home sale contingency',
      rate: formatPercent(pricing.homeSaleContingencyModifier, true),
      positive: true,
    });
  }
  return items;
};

const BrokerFeePreview = ({
  price,
  paymentMethod,
  inspection,
  hasHomeSaleContingency,
}: BrokerFeePreviewProps) => {
  const [feeData, setFeeData] = useState<BrokerFeeState | null>(null);
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);
  const [showWhy, setShowWhy] = useState(false);

  const hasInspection = inspection !== 'none';
  const ready = price > 0 && paymentMethod !== '';

  useEffect(() => {
    if (!ready) {
      setFeeData(null);
      setLoading(false);
      setErrored(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErrored(false);
    const timer = setTimeout(async () => {
      try {
        const result = await buyerClosingCostsService.find({
          propertyPrice: price,
          paymentMethod,
          hasInspection,
          hasHomeSaleContingency,
        });
        if (cancelled) return;
        const other = result?.otherFees;
        if (other?.brokerFee == null) {
          setFeeData(null);
          return;
        }
        setFeeData({
          brokerFee: other.brokerFee,
          brokerFeeEffectiveRate: other.brokerFeeEffectiveRate ?? 0,
          brokerFeeIsMinimum: !!other.brokerFeeIsMinimum,
          brokerFeePricing: other.brokerFeePricing ?? null,
        });
      } catch {
        if (!cancelled) {
          setErrored(true);
          setFeeData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [price, paymentMethod, hasInspection, hasHomeSaleContingency]);

  const breakdown = useMemo(
    () =>
      buildModifierBreakdown(
        paymentMethod,
        inspection,
        hasHomeSaleContingency,
        feeData?.brokerFeePricing ?? null
      ),
    [paymentMethod, inspection, hasHomeSaleContingency, feeData?.brokerFeePricing]
  );

  if (!ready) return null;

  const renderFeeAmount = () => {
    if (errored) return null;
    if (loading || !feeData) {
      return <Skeleton variant="text" width={120} height={40} />;
    }
    return (
      <Typography variant="h5" fontWeight={600}>
        {formatCurrency(feeData.brokerFee)}
      </Typography>
    );
  };

  return (
    <Card
      elevation={0}
      data-testid="broker-fee-preview"
      sx={(theme) => ({
        bgcolor: alpha(theme.palette.primary.main, 0.06),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        borderRadius: 2,
      })}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box
            sx={(theme) => ({
              p: 1.25,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            })}
          >
            <ReceiptLong fontSize="medium" />
          </Box>
          <Box flex={1} minWidth={0}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 1, lineHeight: 1.4 }}
            >
              Your Homepoint Fee
            </Typography>
            <Stack
              direction="row"
              alignItems="baseline"
              spacing={1.5}
              flexWrap="wrap"
              sx={{ mt: 0.25 }}
            >
              {renderFeeAmount()}
              {!loading && feeData && !errored && (
                <Typography variant="body2" color="text.secondary">
                  {formatPercent(feeData.brokerFeeEffectiveRate)} of offer price
                </Typography>
              )}
              {!errored && feeData?.brokerFeeIsMinimum && (
                <Chip
                  label="Minimum fee"
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Stack>
            {errored ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Estimate unavailable. Final fee confirmed at closing.
              </Typography>
            ) : (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                Estimate. Final fee confirmed at closing.
              </Typography>
            )}

            <ButtonBase
              onClick={() => setShowWhy((v) => !v)}
              aria-expanded={showWhy}
              aria-controls={FEE_BREAKDOWN_ID}
              aria-label="Show fee breakdown"
              sx={{
                mt: 1,
                gap: 0.5,
                borderRadius: 1,
                px: 0.5,
                py: 0.25,
                alignSelf: 'flex-start',
              }}
            >
              <Typography
                variant="body2"
                color="primary"
                sx={{ fontWeight: 500 }}
              >
                Why this amount?
              </Typography>
              {showWhy ? (
                <ExpandLess fontSize="small" color="primary" />
              ) : (
                <ExpandMore fontSize="small" color="primary" />
              )}
            </ButtonBase>
            <Collapse in={showWhy} id={FEE_BREAKDOWN_ID}>
              <Stack spacing={0.5} sx={{ mt: 1, pl: 0.5 }}>
                {breakdown.map((item) => (
                  <Stack
                    key={item.label}
                    direction="row"
                    justifyContent="space-between"
                    sx={{ maxWidth: 280 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={item.positive ? 'text.primary' : 'success.main'}
                      fontWeight={500}
                    >
                      {item.rate}
                    </Typography>
                  </Stack>
                ))}
                {feeData?.brokerFeeIsMinimum && feeData.brokerFeePricing && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    Calculated fee is below our{' '}
                    {formatCurrency(feeData.brokerFeePricing.minimumFee)} minimum,
                    so the minimum applies.
                  </Typography>
                )}
              </Stack>
            </Collapse>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default BrokerFeePreview;
